import { FormEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { IgAuthMethod, InstagramAccount } from "../api/types";
import { TIMEZONE_GROUPS } from "../lib/timezones";
import { PROXY_COUNTRIES } from "../lib/proxyCountries";
import { useProxyInfo } from "../hooks/useProxyInfo";
import AuthMethodHelp from "./AuthMethodHelp";

interface Props {
  account: InstagramAccount;
  open: boolean;
  onClose: () => void;
}

/**
 * Modal for editing an existing Instagram account: timezone, password and 2FA login
 * method (interactive / TOTP / backup codes). The TOTP secret and backup codes are
 * write-only on the backend, so on edit we either keep what's saved (when nothing
 * is typed) or fully replace it (when the user provides a new value).
 */
export default function EditAccountModal({ account, open, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const proxyInfoQuery = useProxyInfo();
  const proxyInfo = proxyInfoQuery.data;

  const [timezone, setTimezone] = useState(account.timezone);
  const [password, setPassword] = useState("");
  const [authMethod, setAuthMethod] = useState<IgAuthMethod>(account.authMethod);
  const [totpSecret, setTotpSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState("");
  const [proxyCountry, setProxyCountry] = useState(account.proxyCountry || "");
  const [proxyUrl, setProxyUrl] = useState("");
  // We never echo the user's stored proxy URL back to the UI (it's encrypted
  // server-side). The input is empty by default and only changes the value when
  // the user types something. To clear the override they can type "none" — see
  // the submit handler below.
  const [showAdvancedProxy, setShowAdvancedProxy] = useState(account.hasProxy);

  // Reset local state every time the modal opens so we always start from the
  // server's current snapshot of this account.
  useEffect(() => {
    if (!open) return;
    setTimezone(account.timezone);
    setPassword("");
    setAuthMethod(account.authMethod);
    setTotpSecret("");
    setBackupCodes("");
    setProxyCountry(account.proxyCountry || "");
    setProxyUrl("");
    setShowAdvancedProxy(account.hasProxy);
  }, [
    open,
    account.id,
    account.timezone,
    account.authMethod,
    account.proxyCountry,
    account.hasProxy,
  ]);

  // Esc closes; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api.patch(`/api/instagram-accounts/${account.id}`, body),
    onSuccess: () => {
      toast.success(t("editAccount.toast.saved"));
      qc.invalidateQueries({ queryKey: ["instagram-accounts"] });
      onClose();
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const dirty = useMemo(() => {
    if (timezone !== account.timezone) return true;
    if (password.length > 0) return true;
    if (authMethod !== account.authMethod) return true;
    if (authMethod === "totp" && totpSecret.trim().length > 0) return true;
    if (authMethod === "backup_codes" && backupCodes.trim().length > 0) return true;
    if ((proxyCountry || "") !== (account.proxyCountry || "")) return true;
    if (proxyUrl.trim().length > 0) return true;
    return false;
  }, [
    timezone,
    password,
    authMethod,
    totpSecret,
    backupCodes,
    proxyCountry,
    proxyUrl,
    account.timezone,
    account.authMethod,
    account.proxyCountry,
  ]);

  // Block changing TO totp/backup_codes when no secret is on file AND the user
  // hasn't typed a new one. Matches what the worker actually needs to operate.
  const blockedReason = useMemo(() => {
    if (authMethod === "totp" && !account.hasTotpSecret && totpSecret.trim().length < 8) {
      return t("editAccount.errors.totpRequired");
    }
    if (
      authMethod === "backup_codes" &&
      account.backupCodesRemaining === 0 &&
      backupCodes.trim().length === 0
    ) {
      return t("editAccount.errors.backupCodesRequired");
    }
    return null;
  }, [
    authMethod,
    account.hasTotpSecret,
    account.backupCodesRemaining,
    totpSecret,
    backupCodes,
    t,
  ]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!dirty || blockedReason) return;

    const body: Record<string, unknown> = {};
    if (timezone !== account.timezone) body.timezone = timezone;
    if (password.length > 0) body.password = password;
    if (authMethod !== account.authMethod) body.authMethod = authMethod;
    if (authMethod === "totp" && totpSecret.trim().length > 0) {
      body.totpSecret = totpSecret.trim();
    }
    if (authMethod === "backup_codes" && backupCodes.trim().length > 0) {
      body.backupCodes = backupCodes;
    }
    if ((proxyCountry || "") !== (account.proxyCountry || "")) {
      body.proxyCountry = proxyCountry;
    }
    // Special-case: typing "none" or "clear" wipes the saved override; any other
    // value replaces it. Empty string is treated as "no change".
    const trimmedProxy = proxyUrl.trim();
    if (trimmedProxy.length > 0) {
      const lower = trimmedProxy.toLowerCase();
      if (lower === "none" || lower === "clear" || lower === "default") {
        body.proxyUrl = "";
      } else {
        body.proxyUrl = trimmedProxy;
      }
    }

    save.mutate(body);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={() => !save.isPending && onClose()}
        aria-hidden
      />
      <div className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl border border-slate-800 bg-slate-950 shadow-2xl sm:max-w-xl sm:rounded-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-slate-800 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">
              {t("editAccount.title", { username: account.igUsername })}
            </h2>
            <p className="text-xs text-slate-400">{t("editAccount.subtitle")}</p>
          </div>
          <button
            type="button"
            className="btn-secondary !px-3 !py-1.5"
            onClick={onClose}
            disabled={save.isPending}
            aria-label={t("editAccount.close")}
          >
            ×
          </button>
        </header>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto px-5 py-5"
        >
          <div>
            <label className="label" htmlFor="edit-timezone">
              {t("editAccount.timezone")}
            </label>
            <select
              id="edit-timezone"
              className="input"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONE_GROUPS.map((group) => (
                <optgroup key={group.region} label={group.region}>
                  {group.zones.map((tz) => (
                    <option key={tz.id} value={tz.id}>
                      {tz.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label className="label">{t("editAccount.proxy.heading")}</label>

            {account.effectiveProxySource === "user" && (
              <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 text-xs text-sky-100">
                {t("editAccount.proxy.usingCustom")}
              </div>
            )}

            {account.effectiveProxySource === "managed" && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-100">
                <div className="font-medium">
                  {t("editAccount.proxy.usingManaged.title")}
                </div>
                <p className="mt-0.5 text-emerald-200/80">
                  {t("editAccount.proxy.usingManaged.body", {
                    country: (account.effectiveProxyCountry || "").toUpperCase(),
                  })}
                </p>
              </div>
            )}

            {account.effectiveProxySource === "none" && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">
                {t("editAccount.proxy.noneNotice")}
              </div>
            )}

            {proxyInfo?.enabled && account.effectiveProxySource !== "user" && (
              <div className="mt-2">
                <label className="label text-xs text-slate-400" htmlFor="edit-proxy-country">
                  {t("editAccount.proxy.countryLabel")}
                </label>
                <select
                  id="edit-proxy-country"
                  className="input"
                  value={proxyCountry}
                  onChange={(e) => setProxyCountry(e.target.value)}
                >
                  <option value="">
                    {t("editAccount.proxy.countryAuto", {
                      defaultCountry: (proxyInfo.defaultCountry || "us").toUpperCase(),
                    })}
                  </option>
                  {PROXY_COUNTRIES.filter((c) =>
                    proxyInfo.supportedCountries.includes(c.code)
                  ).map((c) => (
                    <option key={c.code} value={c.code}>
                      {t(`proxy.countries.${c.i18nKey}`, { defaultValue: c.fallback })}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              type="button"
              className="mt-2 text-xs text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
              onClick={() => setShowAdvancedProxy(!showAdvancedProxy)}
            >
              {showAdvancedProxy
                ? t("editAccount.proxy.hideAdvanced")
                : t("editAccount.proxy.showAdvanced")}
            </button>

            {showAdvancedProxy && (
              <div className="mt-2 rounded-lg border border-slate-800 bg-slate-950 p-3">
                <label className="label text-xs text-slate-400" htmlFor="edit-proxy-url">
                  {t("editAccount.proxy.customUrlLabel")}
                </label>
                <input
                  id="edit-proxy-url"
                  type="text"
                  className="input"
                  placeholder={
                    account.hasProxy
                      ? t("editAccount.proxy.customUrlReplacePlaceholder")
                      : t("editAccount.proxy.customUrlPlaceholder")
                  }
                  value={proxyUrl}
                  onChange={(e) => setProxyUrl(e.target.value)}
                  autoComplete="off"
                />
                <p className="mt-1 text-xs text-slate-500">
                  {account.hasProxy
                    ? t("editAccount.proxy.customUrlReplaceHint")
                    : t("editAccount.proxy.customUrlHint")}
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="label" htmlFor="edit-password">
              {t("editAccount.password.label")}
            </label>
            <input
              id="edit-password"
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder={t("editAccount.password.placeholder")}
            />
            <p className="mt-1 text-xs text-slate-500">{t("editAccount.password.hint")}</p>
          </div>

          <div>
            <h3 className="label">{t("editAccount.authMethod.heading")}</h3>
            <p className="-mt-1 mb-3 text-xs text-slate-500">
              {t("editAccount.authMethod.help")}
            </p>

            <div className="space-y-2">
              <AuthOption
                current={authMethod}
                value="interactive"
                title={t("editAccount.authMethod.interactive.title")}
                description={t("editAccount.authMethod.interactive.description")}
                onChange={setAuthMethod}
                help={<AuthMethodHelp method="interactive" />}
              />

              <AuthOption
                current={authMethod}
                value="backup_codes"
                title={t("editAccount.authMethod.backupCodes.title")}
                description={t("editAccount.authMethod.backupCodes.description")}
                onChange={setAuthMethod}
                help={<AuthMethodHelp method="backup_codes" />}
              >
                {account.backupCodesRemaining > 0 && (
                  <p className="mt-2 text-xs text-emerald-300">
                    {t("editAccount.authMethod.backupCodes.savedCount", {
                      count: account.backupCodesRemaining,
                    })}
                  </p>
                )}
                <textarea
                  className="input mt-2 font-mono"
                  rows={4}
                  placeholder={
                    account.backupCodesRemaining > 0
                      ? t("editAccount.authMethod.backupCodes.replacePlaceholder")
                      : t("editAccount.authMethod.backupCodes.placeholder")
                  }
                  value={backupCodes}
                  onChange={(e) => setBackupCodes(e.target.value)}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {account.backupCodesRemaining > 0
                    ? t("editAccount.authMethod.backupCodes.replaceHint")
                    : t("editAccount.authMethod.backupCodes.hint")}
                </p>
              </AuthOption>

              <AuthOption
                current={authMethod}
                value="totp"
                title={t("editAccount.authMethod.totp.title")}
                description={t("editAccount.authMethod.totp.description")}
                onChange={setAuthMethod}
                help={<AuthMethodHelp method="totp" />}
              >
                {account.hasTotpSecret && (
                  <p className="mt-2 text-xs text-emerald-300">
                    {t("editAccount.authMethod.totp.saved")}
                  </p>
                )}
                <input
                  type="text"
                  className="input mt-2 font-mono"
                  placeholder={
                    account.hasTotpSecret
                      ? t("editAccount.authMethod.totp.replacePlaceholder")
                      : t("editAccount.authMethod.totp.placeholder")
                  }
                  value={totpSecret}
                  onChange={(e) => setTotpSecret(e.target.value)}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {account.hasTotpSecret
                    ? t("editAccount.authMethod.totp.replaceHint")
                    : t("editAccount.authMethod.totp.hint")}
                </p>
              </AuthOption>
            </div>
          </div>

          {blockedReason && (
            <p className="text-xs text-rose-300">{blockedReason}</p>
          )}
        </form>

        <footer className="flex flex-col-reverse gap-2 border-t border-slate-800 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={save.isPending}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={!dirty || !!blockedReason || save.isPending}
          >
            {save.isPending ? t("common.saving") : t("common.save")}
          </button>
        </footer>
      </div>
    </div>
  );
}

interface AuthOptionProps {
  current: IgAuthMethod;
  value: IgAuthMethod;
  title: string;
  description: string;
  onChange: (v: IgAuthMethod) => void;
  /** Always rendered (e.g. step-by-step help). */
  help?: React.ReactNode;
  /** Only rendered when this option is selected (e.g. an input). */
  children?: React.ReactNode;
}

function AuthOption({
  current,
  value,
  title,
  description,
  onChange,
  help,
  children,
}: AuthOptionProps) {
  const selected = current === value;
  return (
    <label
      className={clsx(
        "block cursor-pointer rounded-lg border p-3 transition",
        selected
          ? "border-brand-400 bg-brand-500/5"
          : "border-slate-800 bg-slate-950 hover:border-slate-700"
      )}
    >
      <div className="flex items-start gap-3">
        <input
          type="radio"
          className="mt-1"
          checked={selected}
          onChange={() => onChange(value)}
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{title}</div>
          <div className="mt-0.5 text-xs text-slate-400">{description}</div>
        </div>
      </div>
      {help}
      {selected && children}
    </label>
  );
}
